import type { ComponentType, ReactNode, FC } from 'react';
import { useCallback, useContext, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

import { ModuleName } from '@wix/da-gruser-shared/pkg/types/modules';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import Flex from '@wix/da-ds/pkg/Flex';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import MeasuredGrid, { GridElement } from '@wix/da-shared-react/pkg/Grid';
import { getGridConfiguration } from '@wix/da-shared-react/pkg/Grid/helpers';

import { ShopItemType } from '../../../../../../types/shop';
import withSectionFetchSpinner from '../../../../../decorators/withSectionFetchSpinner';
import LoadableDuperbrowseContext from '../../../../../contexts/LoadableDuperbrowseContext';
import StreamPagination from '../../../_partials/StreamPagination';
import { DEFAULT_GRID_ELEMENT_HEIGHT } from './constants';
import { RenderCardProps, ShopCardSubject } from './types';
import useShopGridStream from './useShopGridStream';
import UploadMoreSubmitButton from './UploadMoreSubmitButton';
import ShopFilters from './ShopFilters';
import { getBreakpoints } from './breakpoints';
import { isCurrentUserProfileOwner } from '../../../../../selectors/users';
import { useSelector } from 'react-redux';
import { FolderUploadMore } from '../UploadMore/UploadMore';

export interface Props {
  itemType: ShopItemType;
  moduleName: ModuleName;
  uploadMoreBiClickInfo: string;
  invitationToUpload: ComponentType;
  className?: string;
  gridElementHeight?: number;
  renderCard: (props: RenderCardProps) => ReactNode;
  /** Content to show after a specific row. If there aren't enough rows, shows after the grid. */
  contentAfterRow?: ReactNode;
  /** The row index after which to show contentAfterRow (0-indexed). Default: 1 (after 2nd row) */
  contentAfterRowIndex?: number;
}

export const ShopGridWithFilters: FC<Props> = ({
  itemType,
  moduleName,
  uploadMoreBiClickInfo,
  invitationToUpload: InvitationToUpload,
  gridElementHeight = DEFAULT_GRID_ELEMENT_HEIGHT,
  className,
  renderCard,
  contentAfterRow,
  contentAfterRowIndex = 1,
}) => {
  const { t } = useTranslation();
  const isDesktop = !useContext(MobileContext);
  const exclusives_newstyle = useFlag('exclusives_newstyle');
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const [areFiltersExpanded, setFiltersExpanded] = useState(isDesktop);
  const isOwner = useSelector(isCurrentUserProfileOwner);
  const breakpoints = useMemo(() => getBreakpoints(gridElementHeight), [gridElementHeight]);

  const {
    streamId,
    items: streamItems = [],
    total = 0,
    hasLess,
  } = useShopGridStream<ShopCardSubject | FolderUploadMore>(itemType);
  let items = streamItems;
  const hasProductsForSale = total >= 1;

  let showUploadMoreInGrid = isOwner && !hasLess;

  // on desktop, show on non-empty state
  if (isDesktop) {
    showUploadMoreInGrid = showUploadMoreInGrid && items.length > 0;
  } else {
    // on mobile, show only on commissions page since it's only entry point for mobile
    showUploadMoreInGrid =
      showUploadMoreInGrid && (itemType === ShopItemType.COMMISSIONS || items.length === 0);
  }

  // prefix an "upload more" card at the beginning of the list
  if (showUploadMoreInGrid) {
    items = [
      {
        uploadMore: (
          <UploadMoreSubmitButton
            itemType={itemType}
            biClickInfo={uploadMoreBiClickInfo}
            view={'small'}
          />
        ),
      },
      ...items,
    ];
  }

  // Calculate number of rows to determine where to show contentAfterRow
  const { elementsPerRow } = getGridConfiguration({
    breakpoints,
    screenWidth,
    preserveAspectRatio: false,
  });
  const estimatedRows = Math.ceil(items.length / elementsPerRow);
  // Target row is 0-indexed, so contentAfterRowIndex=1 means after 2nd row
  // To show content "after" row N, we pass contentStripIndex=N+1 to Grid
  // (because Grid renders contentStrip BEFORE the row's items)
  const targetGridRowIndex = contentAfterRowIndex + 1;
  // If we have enough rows, use Grid's contentStrip. Otherwise, render after the grid.
  const hasEnoughRowsForInlineContent = contentAfterRow && estimatedRows > contentAfterRowIndex + 1;
  const showContentAfterGrid = contentAfterRow && !hasEnoughRowsForInlineContent;

  const renderElement = useCallback(
    (element: GridElement) => {
      const { index, style } = element;

      if (index === 0 && 'uploadMore' in items[index]) {
        return (
          <div style={{ ...style, height: 'auto' }}>
            {(items[index] as FolderUploadMore).uploadMore}
          </div>
        );
      }
      return renderCard({
        subject: items[element.index] as ShopCardSubject,
        ...element,
      });
    },
    [items, renderCard]
  );

  return (
    <Flex className={className} direction="column" gap={24}>
      {hasProductsForSale && (
        <ErrorBoundary debugComponent="ShopGridWithFilters">
          <ShopFilters
            moduleName={moduleName}
            itemCount={items.length}
            // these are here and not just inside ShopFilters because we
            // often need to know the expanded state to re-calc breakpoints
            // TODO: maybe design will be ok with the same elementcount
            // regardless of expanded or not
            areFiltersExpanded={areFiltersExpanded}
            setFiltersExpanded={setFiltersExpanded}
            itemType={itemType}
          >
            <LoadableDuperbrowseContext
              streamId={streamId}
              parent={{ title: t('duperbrowse.backButton.profile.shop') }}
            >
              <MeasuredGrid
                cookieType={MeasuredCookieType.USER_PROFILE_SHOP}
                breakpoints={breakpoints}
                preserveAspectRatio={false}
                enableScrollOptim
                elementCount={items.length}
                renderElement={renderElement}
                contentVisibilityFallback={!!exclusives_newstyle}
                contentStrip={hasEnoughRowsForInlineContent ? contentAfterRow : undefined}
                contentStripIndex={hasEnoughRowsForInlineContent ? targetGridRowIndex : undefined}
              />
            </LoadableDuperbrowseContext>

            {showContentAfterGrid && contentAfterRow}

            <StreamPagination streamId={streamId} />
          </ShopFilters>
        </ErrorBoundary>
      )}

      {!hasProductsForSale && <InvitationToUpload />}
    </Flex>
  );
};

ShopGridWithFilters.displayName = 'ShopGridWithFilters';

const enhance = compose(withSectionFetchSpinner);
export default enhance(ShopGridWithFilters);
